import type { ActiveSleepSession, LogEvent } from "../types/domain";
import { getNormForWeek } from "../../data/norms";
import { getWakeWindowNormForWeek, type WakeWindowRange } from "../../data/wakeWindowNorms";

export type SleepSessionType = "nap" | "night";
export type WakeWindowBucket = "napPostNap" | "napPostNight" | "night";

export interface ClassifiedSleepSession {
  logId: string;
  startMs: number;
  endMs: number;
  durationMin: number;
  type: SleepSessionType;
}

export interface WakeWindowSample {
  bucket: WakeWindowBucket;
  gapMin: number;
  gapEndMs: number;
  // 1-based position of the destination nap within its day (1 = first nap after overnight
  // sleep, 2 = second, ...). Undefined for "night" bucket samples, which don't need it.
  napOrdinal?: number;
}

export interface WakeWindowStat {
  sampleCount: number;
  medianMin: number | null;
  iqrMin: number | null;
  trimmedMeanMin: number | null;
}

// "ready" is a tight, high-confidence window; "rough" is a wider fallback used whenever the
// data isn't good enough for "ready" — there's always at least the published age norm to lean
// on, so a prediction row never has nothing to show once any sleep has been logged at all.
export type SleepEstimateStatus = "ready" | "rough";

export interface BlendedWindow {
  status: SleepEstimateStatus;
  centerMin: number;
  halfWidthMin: number;
  sampleCount: number;
}

export interface SleepWindowPrediction {
  status: SleepEstimateStatus;
  windowStartMs: number;
  windowEndMs: number;
}

export interface SleepPrediction {
  nextNap: SleepWindowPrediction | null;
  nextBedtime: SleepWindowPrediction;
}

export type NextSleepEventKind = "nap" | "night";

export interface NextSleepEvent {
  kind: NextSleepEventKind;
  window: SleepWindowPrediction;
}

// A daytime-started session this long or longer is treated as overnight sleep too
// (almost never a legitimate single nap; usually a late bedtime or a merged log entry).
export const NIGHT_DURATION_THRESHOLD_MIN = 240;

// Wake-window gaps outside these bounds are discarded (not clamped) as likely missed logs
// rather than real wake windows, so a bad data point never quietly biases the average.
export const NAP_GAP_MIN_MIN = 20;
export const NAP_GAP_MAX_MIN = 360;
export const NIGHT_GAP_MIN_MIN = 45;
export const NIGHT_GAP_MAX_MIN = 480;

export const LOOKBACK_DAYS = 7;
export const MIN_SAMPLES_TRUSTED = 3;
// The night bucket can have at most one sample per day, so this must stay reachable
// within LOOKBACK_DAYS (7) — it's intentionally lower than a "10 out of 14" bar would be.
export const FULL_TRUST_SAMPLE_COUNT = 6;
export const MAX_PERSONAL_WEIGHT = 0.85;
export const NAPLESS_TRANSITION_MIN_SLEEP_LOGS = 5;

// The wake window right after a long night's sleep tends to run shorter than the wake
// windows between the naps that follow later in the day (the baby "recharges" less the
// longer they've stay awake). This nudges only the age-norm baseline used for the
// napPostNight bucket; the pre-bedtime window is already modeled as longer via the
// separate, wider `night` entry in wakeWindowNorms.ts, so no equivalent nudge is needed there.
export const POST_NIGHT_WAKE_WINDOW_OFFSET_MIN = 30;
export const MIN_WAKE_WINDOW_NORM_MIN = 30;

// A "ready" window is only ever shown this tight or tighter; anything wider falls back to a
// "rough" one instead of claiming false precision.
export const DISPLAY_RANGE_MAX_WIDTH_MIN = 15;
// Floor so a perfectly consistent baby doesn't get an implausible zero-width "2:00 - 2:00 PM".
export const DISPLAY_RANGE_MIN_WIDTH_MIN = 10;

// Bounds for the "rough" fallback window — wider than the "ready" cap (so it reads as an
// estimate, not a promise) but still bounded (so it's never a multi-hour, useless range).
export const ROUGH_DISPLAY_RANGE_MAX_WIDTH_MIN = 90;
export const ROUGH_DISPLAY_RANGE_MIN_WIDTH_MIN = 30;

const MS_PER_MIN = 60000;
const MS_PER_DAY = 24 * 60 * 60 * 1000;
const MINUTES_PER_DAY = 1440;
const HALF_DAY_MIN = 720;

function resolveEndMs(startMs: number, endedAt?: string, durationMin?: number): number | null {
  if (endedAt) return new Date(endedAt).getTime();
  if (durationMin != null) return startMs + durationMin * MS_PER_MIN;
  return null;
}

// The user's Sleep Day Cutoff setting (the hour a new "sleep day" begins, e.g. 7:00 AM)
// doubles as the boundary between nap-time and night: night runs the 12 hours starting
// exactly 12 hours before the cutoff, wrapping through midnight, and ends at the cutoff.
function isInNightWindow(minutesOfDay: number, cutoffMinutes: number): boolean {
  const nightStartMin = (cutoffMinutes + HALF_DAY_MIN) % MINUTES_PER_DAY;
  const offset = ((minutesOfDay - nightStartMin) % MINUTES_PER_DAY + MINUTES_PER_DAY) % MINUTES_PER_DAY;
  return offset < HALF_DAY_MIN;
}

export function classifySleepSession(log: LogEvent, cutoffMinutes: number): ClassifiedSleepSession | null {
  if (log.type !== "sleep") return null;
  const startMs = new Date(log.occurredAt).getTime();
  const endMs = resolveEndMs(startMs, log.payload.endedAt, log.payload.durationMin);
  if (!Number.isFinite(startMs) || endMs == null || !Number.isFinite(endMs) || endMs <= startMs) return null;

  const durationMin = (endMs - startMs) / MS_PER_MIN;
  const startDate = new Date(startMs);
  const minutesOfDay = startDate.getHours() * 60 + startDate.getMinutes();
  const startsInNightWindow = isInNightWindow(minutesOfDay, cutoffMinutes);
  const type: SleepSessionType = startsInNightWindow || durationMin >= NIGHT_DURATION_THRESHOLD_MIN ? "night" : "nap";

  return { logId: log.id, startMs, endMs, durationMin, type };
}

// Ascending by start time; sessions without a resolvable end are excluded (no anchor to
// compute a following wake-window gap from).
export function classifySleepLogs(logs: LogEvent[], cutoffMinutes: number): ClassifiedSleepSession[] {
  const sessions: ClassifiedSleepSession[] = [];
  for (const log of logs) {
    const session = classifySleepSession(log, cutoffMinutes);
    if (session) sessions.push(session);
  }
  return sessions.sort((a, b) => a.startMs - b.startMs);
}

// The 1-based count of consecutive naps since the most recent night session (0 for night
// sessions themselves, which reset the count for whatever nap follows them). Naps deeper into
// the day systematically have different wake windows than earlier ones (see napPostNap below),
// so this lets same-position naps be compared to each other instead of pooled regardless of
// how far into the day they fall.
export function napOrdinalsForSessions(sessions: ClassifiedSleepSession[]): number[] {
  let ordinal = 0;
  return sessions.map((s) => {
    ordinal = s.type === "night" ? 0 : ordinal + 1;
    return ordinal;
  });
}

export function computeWakeWindowSamples(sessions: ClassifiedSleepSession[]): WakeWindowSample[] {
  const ordinals = napOrdinalsForSessions(sessions);
  const samples: WakeWindowSample[] = [];
  for (let i = 1; i < sessions.length; i++) {
    const prev = sessions[i - 1];
    const curr = sessions[i];
    const gapMin = (curr.startMs - prev.endMs) / MS_PER_MIN;
    if (gapMin <= 0) continue;

    if (curr.type === "night") {
      if (gapMin < NIGHT_GAP_MIN_MIN || gapMin > NIGHT_GAP_MAX_MIN) continue;
      samples.push({ bucket: "night", gapMin, gapEndMs: curr.startMs });
    } else {
      if (gapMin < NAP_GAP_MIN_MIN || gapMin > NAP_GAP_MAX_MIN) continue;
      const bucket: WakeWindowBucket = prev.type === "night" ? "napPostNight" : "napPostNap";
      samples.push({ bucket, gapMin, gapEndMs: curr.startMs, napOrdinal: ordinals[i] });
    }
  }
  return samples;
}

function median(sorted: number[]): number | null {
  if (sorted.length === 0) return null;
  const mid = Math.floor(sorted.length / 2);
  return sorted.length % 2 !== 0 ? sorted[mid] : (sorted[mid - 1] + sorted[mid]) / 2;
}

function percentile(sorted: number[], p: number): number {
  if (sorted.length === 1) return sorted[0];
  const idx = (p / 100) * (sorted.length - 1);
  const lower = Math.floor(idx);
  const upper = Math.ceil(idx);
  if (lower === upper) return sorted[lower];
  return sorted[lower] + (sorted[upper] - sorted[lower]) * (idx - lower);
}

function trimmedMean(sorted: number[], trimFraction: number): number | null {
  if (sorted.length === 0) return null;
  const trimCount = Math.floor(sorted.length * trimFraction);
  const trimmed = sorted.slice(trimCount, sorted.length - trimCount);
  const pool = trimmed.length > 0 ? trimmed : sorted;
  return pool.reduce((sum, v) => sum + v, 0) / pool.length;
}

export function aggregateWakeWindowStat(
  samples: WakeWindowSample[],
  bucket: WakeWindowBucket,
  nowMs: number,
  lookbackDays = LOOKBACK_DAYS,
  napOrdinal?: number
): WakeWindowStat {
  const bucketSamples = samples.filter(
    (s) => s.bucket === bucket && (napOrdinal === undefined || s.napOrdinal === napOrdinal)
  );
  const windowed = bucketSamples.filter((s) => nowMs - s.gapEndMs <= lookbackDays * MS_PER_DAY);
  const sorted = windowed.map((s) => s.gapMin).sort((a, b) => a - b);
  return {
    sampleCount: sorted.length,
    medianMin: median(sorted),
    iqrMin: sorted.length > 0 ? percentile(sorted, 75) - percentile(sorted, 25) : null,
    trimmedMeanMin: trimmedMean(sorted, 0.1),
  };
}

function clamp(value: number, min: number, max: number): number {
  return Math.max(min, Math.min(max, value));
}

// The center blends the baby's own median with the age-norm center, weighted by how much
// personal data exists — even a sample or two below the "trusted" bar still nudges it a little,
// since a rough guess informed by the baby's own pattern beats a pure age-norm guess. Only a
// trusted, tight (≤ DISPLAY_RANGE_MAX_WIDTH_MIN) personal spread earns "ready"; anything else
// falls back to "rough" rather than showing nothing — untrusted personal data uses the norm's
// own published range as the rough width, since there isn't a reliable personal spread to use.
export function blendWithNorm(stat: WakeWindowStat, norm: WakeWindowRange): BlendedWindow {
  const normCenter = (norm.minMin + norm.maxMin) / 2;
  const isTrusted = stat.sampleCount >= MIN_SAMPLES_TRUSTED && stat.medianMin != null;
  const personalWeight = stat.medianMin != null ? Math.min(MAX_PERSONAL_WEIGHT, stat.sampleCount / FULL_TRUST_SAMPLE_COUNT) : 0;
  const centerMin = personalWeight * (stat.medianMin ?? normCenter) + (1 - personalWeight) * normCenter;

  if (isTrusted) {
    const rawHalfWidthMin = (stat.iqrMin ?? 0) / 2;
    if (rawHalfWidthMin * 2 <= DISPLAY_RANGE_MAX_WIDTH_MIN) {
      const halfWidthMin = clamp(rawHalfWidthMin, DISPLAY_RANGE_MIN_WIDTH_MIN / 2, DISPLAY_RANGE_MAX_WIDTH_MIN / 2);
      return { status: "ready", centerMin, halfWidthMin, sampleCount: stat.sampleCount };
    }
    const halfWidthMin = clamp(rawHalfWidthMin, ROUGH_DISPLAY_RANGE_MIN_WIDTH_MIN / 2, ROUGH_DISPLAY_RANGE_MAX_WIDTH_MIN / 2);
    return { status: "rough", centerMin, halfWidthMin, sampleCount: stat.sampleCount };
  }

  const halfWidthMin = clamp((norm.maxMin - norm.minMin) / 2, ROUGH_DISPLAY_RANGE_MIN_WIDTH_MIN / 2, ROUGH_DISPLAY_RANGE_MAX_WIDTH_MIN / 2);
  return { status: "rough", centerMin, halfWidthMin, sampleCount: stat.sampleCount };
}

// Shortens a nap wake-window norm for use right after overnight sleep (see
// POST_NIGHT_WAKE_WINDOW_OFFSET_MIN above), floored so young-infant ranges never invert.
export function postNightNapNorm(nap: WakeWindowRange): WakeWindowRange {
  return {
    minMin: Math.max(MIN_WAKE_WINDOW_NORM_MIN, nap.minMin - POST_NIGHT_WAKE_WINDOW_OFFSET_MIN),
    maxMin: Math.max(MIN_WAKE_WINDOW_NORM_MIN, nap.maxMin - POST_NIGHT_WAKE_WINDOW_OFFSET_MIN),
  };
}

function windowFromBlend(anchorMs: number, blend: BlendedWindow): SleepWindowPrediction {
  return {
    status: blend.status,
    windowStartMs: anchorMs + (blend.centerMin - blend.halfWidthMin) * MS_PER_MIN,
    windowEndMs: anchorMs + (blend.centerMin + blend.halfWidthMin) * MS_PER_MIN,
  };
}

// Predicts the next nap and next bedtime windows from the baby's wake-window history,
// blended with age-based norms. Returns null when there's nothing to predict from (no
// sleep session logged yet) or nothing useful to predict right now (baby is asleep).
// Each row's prediction reports "rough" instead of "ready" when the data isn't
// consistent/plentiful enough to narrow it to DISPLAY_RANGE_MAX_WIDTH_MIN or tighter.
export function predictNextSleep(
  logs: LogEvent[],
  ageWeeks: number,
  nowMs: number,
  activeSleepSession: ActiveSleepSession | null,
  cutoffMinutes: number
): SleepPrediction | null {
  if (activeSleepSession) return null;

  const sessions = classifySleepLogs(logs.filter((l) => l.type === "sleep"), cutoffMinutes);
  if (sessions.length === 0) return null;

  const lastSession = sessions[sessions.length - 1];
  const lastOrdinal = napOrdinalsForSessions(sessions).at(-1) ?? 0;
  const samples = computeWakeWindowSamples(sessions);
  const wakeWindowNorm = getWakeWindowNormForWeek(ageWeeks);

  const napBucket: WakeWindowBucket = lastSession.type === "night" ? "napPostNight" : "napPostNap";
  const napNorm = napBucket === "napPostNight" ? postNightNapNorm(wakeWindowNorm.nap) : wakeWindowNorm.nap;

  // Prefer history from the same nap-of-day slot the upcoming nap will fall into (e.g. always
  // the 3rd nap) over the broader, position-agnostic bucket, once there's enough of it — a 2nd
  // nap's wake window and a 4th nap's are naturally different lengths, and pooling them widens
  // the spread past DISPLAY_RANGE_MAX_WIDTH_MIN even with plenty of days logged.
  const pooledNapStat = aggregateWakeWindowStat(samples, napBucket, nowMs);
  const ordinalNapStat =
    napBucket === "napPostNap" ? aggregateWakeWindowStat(samples, napBucket, nowMs, LOOKBACK_DAYS, lastOrdinal + 1) : null;
  const napStat = ordinalNapStat && ordinalNapStat.sampleCount >= MIN_SAMPLES_TRUSTED ? ordinalNapStat : pooledNapStat;
  const napBlend = blendWithNorm(napStat, napNorm);
  const nightBlend = blendWithNorm(aggregateWakeWindowStat(samples, "night", nowMs), wakeWindowNorm.night);

  const lookbackStartMs = nowMs - LOOKBACK_DAYS * MS_PER_DAY;
  const recentSessions = sessions.filter((s) => s.startMs >= lookbackStartMs);
  const recentNaps = recentSessions.filter((s) => s.type === "nap");
  const hasStoppedNapping =
    getNormForWeek(ageWeeks).sleepNapsPerDay <= 1 &&
    recentNaps.length === 0 &&
    recentSessions.length >= NAPLESS_TRANSITION_MIN_SLEEP_LOGS;

  return {
    nextNap: hasStoppedNapping ? null : windowFromBlend(lastSession.endMs, napBlend),
    nextBedtime: windowFromBlend(lastSession.endMs, nightBlend),
  };
}

// The nap and bedtime rows predict two different possible next sleeps, but only one of them
// will actually happen next, so the UI shows a single combined row rather than both — whichever
// window starts sooner, regardless of whether it's "ready" or only "rough" (both are always
// populated; see blendWithNorm), since even a rough guess at the sooner event is more useful
// than a more confident guess at the one that won't happen first.
export function selectNextSleepEvent(prediction: SleepPrediction): NextSleepEvent {
  const { nextNap, nextBedtime } = prediction;
  if (!nextNap) return { kind: "night", window: nextBedtime };
  return nextNap.windowStartMs <= nextBedtime.windowStartMs
    ? { kind: "nap", window: nextNap }
    : { kind: "night", window: nextBedtime };
}
